import { useState } from 'react';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Select, Tag } from 'antd';
import { api } from '../api';
import { Bone, DataTable, ErrorNotice, PageHeader, Panel } from '../components';
import { actionLabel, formatDateTime, mobileLabel, timeAgo, useLoad } from '../lib';
const AREA_COLORS = {
    account: 'blue',
    licence: 'green',
    device: 'orange',
    plan: 'magenta',
    payment: 'gold',
    admin: 'red',
    settings: 'purple',
    notification: 'cyan',
    storage: 'purple',
    sync: 'geekblue'
};
function MetaChips({ meta }) {
    const entries = Object.entries(meta ?? {});
    if (entries.length === 0)
        return <span className="cell-sub">—</span>;
    return (<span className="meta-chips">
      {entries.map(([key, value]) => (<span className="meta-chip" key={key} title={`${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`}>
          <b>{key}</b>
          {Array.isArray(value) ? value.join(', ') || '—' : typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
        </span>))}
    </span>);
}
export function AuditPage() {
    const [search, setSearch] = useState('');
    const [area, setArea] = useState('all');
    const { data, error, loading, reload } = useLoad(async () => {
        const [audit, accounts] = await Promise.all([api('/v1/admin/audit'), api('/v1/admin/accounts')]);
        return { events: audit.events, accounts: new Map(accounts.accounts.map((account) => [account.id, account])) };
    });
    const events = data?.events ?? [];
    /** The account an event is about: its name and mobile (accounts may have no email now). */
    const who = (event) => {
        const account = event.accountId ? data?.accounts.get(event.accountId) : undefined;
        return account ? { name: account.name, detail: mobileLabel(account.mobile) || account.email || '' } : event.accountEmail ? { name: event.accountEmail, detail: '' } : null;
    };
    const areas = [...new Set(events.map((event) => event.action.split('.')[0] ?? ''))].filter(Boolean).sort();
    const needle = search.trim().toLowerCase();
    const shown = events.filter((event) => {
        if (area !== 'all' && !event.action.startsWith(`${area}.`))
            return false;
        if (!needle)
            return true;
        return [event.action, actionLabel(event.action), event.accountEmail ?? '', who(event)?.name ?? '', who(event)?.detail ?? '', JSON.stringify(event.meta)].some((text) => text.toLowerCase().includes(needle));
    });
    return (<>
      <PageHeader title="Audit log" description="Every change made through Kivonex Cloud, newest first (latest 200)." actions={<Button icon={<ReloadOutlined />} onClick={() => void reload()} loading={loading}>
            Refresh
          </Button>}/>
      <ErrorNotice error={error}/>
      <Panel flush>
        <div className="toolbar">
          <div className="toolbar-group">
            <Input allowClear prefix={<SearchOutlined style={{ color: 'var(--muted)' }}/>} placeholder="Search action, account or detail" value={search} onChange={(event) => setSearch(event.target.value)} style={{ width: 320, maxWidth: '100%' }}/>
            <Select value={area} onChange={setArea} style={{ width: 180 }} options={[{ value: 'all', label: 'All areas' }, ...areas.map((value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }))]}/>
          </div>
          {loading && !data ? <Bone width={110} height={11}/> : <span className="cell-sub">{shown.length} of {events.length} events</span>}
        </div>
        <DataTable rowKey="id" loading={loading} skeletonRows={8} dataSource={shown} pagination={shown.length > 25 ? { pageSize: 25, showSizeChanger: false } : false} scroll={{ x: 900 }} locale={{ emptyText: events.length ? 'No events match.' : 'No events yet.' }} columns={[
            {
                title: 'When',
                dataIndex: 'at',
                width: 190,
                skeleton: 'lines',
                render: (value) => (<span>
                  {timeAgo(value)}
                  <span className="cell-sub num">{formatDateTime(value)}</span>
                </span>)
            },
            {
                title: 'Action',
                dataIndex: 'action',
                width: 240,
                skeleton: 'lines',
                render: (value) => (<span>
                  <Tag color={AREA_COLORS[value.split('.')[0] ?? ''] ?? 'default'} variant="filled">
                    {actionLabel(value)}
                  </Tag>
                  <span className="cell-sub mono">{value}</span>
                </span>)
            },
            {
                title: 'Account',
                width: 240,
                render: (_value, event) => {
                    const account = who(event);
                    return account ? (<span>
                    {account.name}
                    {account.detail && <span className="cell-sub num">{account.detail}</span>}
                  </span>) : (<span className="cell-sub">System</span>);
                }
            },
            { title: 'Details', dataIndex: 'meta', render: (value) => <MetaChips meta={value}/> }
        ]}/>
      </Panel>
    </>);
}
